/* eslint-disable react-refresh/only-export-components */
import React, { lazy, Suspense } from 'react';
import type { RouteObject } from 'react-router-dom';

import { motion } from 'framer-motion';

import { pageContentVariants } from '@/animations';
import ErrorBoundary from '@/components/ErrorBoundary';
import ProtectedRoute from '@/components/ProtectedRoute';
import {
  AuthSkeleton,
  CheckoutSkeleton,
  ContentPageSkeleton,
  ProductDetailsSkeleton,
  ProductGridSkeleton,
} from '@/components/Skeleton';

const LoaderSkeleton = ({ variant }: { variant?: string }) => {
  switch (variant) {
    case 'auth':
      return <AuthSkeleton />;
    case 'checkout':
      return <CheckoutSkeleton />;
    case 'product':
      return <ProductDetailsSkeleton />;
    case 'shop':
      return <ProductGridSkeleton count={8} />;
    case 'content':
      return <ContentPageSkeleton />;
    default:
      return (
        <div className="mx-auto w-full max-w-6xl animate-pulse space-y-8 px-4 py-8">
          <div className="mx-auto mb-6 h-8 w-64 rounded-2xl bg-neutral-200 dark:bg-neutral-800" />
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            <div className="h-48 rounded-3xl bg-neutral-200 dark:bg-neutral-800" />
            <div className="h-48 rounded-3xl bg-neutral-200 dark:bg-neutral-800" />
            <div className="h-48 rounded-3xl bg-neutral-200 dark:bg-neutral-800" />
          </div>
          <div className="h-64 rounded-3xl bg-neutral-200 dark:bg-neutral-800" />
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <div className="h-40 rounded-3xl bg-neutral-200 dark:bg-neutral-800" />
            <div className="h-40 rounded-3xl bg-neutral-200 dark:bg-neutral-800" />
          </div>
        </div>
      );
  }
};

const PageWrapper: React.FC<{ children: React.ReactNode; skeleton?: string }> = ({
  children,
  skeleton = 'default',
}) => (
  <ErrorBoundary>
    <Suspense fallback={<LoaderSkeleton variant={skeleton} />}>
      <motion.div
        variants={pageContentVariants}
        initial="initial"
        animate="animate"
        className="w-full">
        {children}
      </motion.div>
    </Suspense>
  </ErrorBoundary>
);

const Home = lazy(() => import('@/pages/Home'));
const Shop = lazy(() => import('@/pages/Shop'));
const ProductDetails = lazy(() => import('@/pages/ProductDetails/ProductDetails'));
const Cart = lazy(() => import('@/pages/Cart/Cart'));
const Checkout = lazy(() => import('@/pages/Checkout/Checkout'));
const OrderSuccess = lazy(() => import('@/pages/OrderHistory/OrderSuccess'));
const Contact = lazy(() => import('@/pages/Contact/Contact'));
const About = lazy(() => import('@/pages/About/About'));
const FAQ = lazy(() => import('@/pages/FAQ/FAQ'));
const ShippingReturns = lazy(() => import('@/pages/Guide/ShippingReturns'));
const SizeGuide = lazy(() => import('@/pages/Guide/SizeGuide'));
const CareInstructions = lazy(() => import('@/pages/Guide/CareInstructions'));
const GiftGuide = lazy(() => import('@/pages/Guide/GiftGuide'));
const PrivacyPolicy = lazy(() => import('@/pages/Legal/PrivacyPolicy'));
const TermsOfService = lazy(() => import('@/pages/Legal/TermsOfService'));
const SearchResults = lazy(() => import('@/pages/SearchResults'));
const Login = lazy(() => import('@/pages/Auth/Login'));
const Register = lazy(() => import('@/pages/Auth/Register'));
const ForgotPassword = lazy(() => import('@/pages/Auth/ForgotPassword'));
const ResetPassword = lazy(() => import('@/pages/Auth/ResetPassword'));
const Account = lazy(() => import('@/pages/Account/Account'));
const OrderHistory = lazy(() => import('@/pages/OrderHistory/OrderHistory'));
const Wishlist = lazy(() => import('@/pages/Wishlist/Wishlist'));
const NotFound = lazy(() => import('@/pages/NotFound'));

export const routes: RouteObject[] = [
  {
    path: '/',
    element: (
      <PageWrapper skeleton="default">
        <Home />
      </PageWrapper>
    ),
  },
  {
    path: '/shop',
    element: (
      <PageWrapper skeleton="shop">
        <Shop />
      </PageWrapper>
    ),
  },
  {
    path: '/product/:id',
    element: (
      <PageWrapper skeleton="product">
        <ProductDetails />
      </PageWrapper>
    ),
  },
  {
    path: '/cart',
    element: (
      <PageWrapper skeleton="default">
        <Cart />
      </PageWrapper>
    ),
  },
  {
    path: '/checkout',
    element: (
      <PageWrapper skeleton="checkout">
        <Checkout />
      </PageWrapper>
    ),
  },
  {
    path: '/order-success',
    element: (
      <PageWrapper skeleton="default">
        <OrderSuccess />
      </PageWrapper>
    ),
  },
  {
    path: '/contact',
    element: (
      <PageWrapper skeleton="content">
        <Contact />
      </PageWrapper>
    ),
  },
  {
    path: '/about',
    element: (
      <PageWrapper skeleton="content">
        <About />
      </PageWrapper>
    ),
  },
  {
    path: '/faq',
    element: (
      <PageWrapper skeleton="content">
        <FAQ />
      </PageWrapper>
    ),
  },
  {
    path: '/shipping-returns',
    element: (
      <PageWrapper skeleton="content">
        <ShippingReturns />
      </PageWrapper>
    ),
  },
  {
    path: '/size-guide',
    element: (
      <PageWrapper skeleton="content">
        <SizeGuide />
      </PageWrapper>
    ),
  },
  {
    path: '/care-instructions',
    element: (
      <PageWrapper skeleton="content">
        <CareInstructions />
      </PageWrapper>
    ),
  },
  {
    path: '/gift-guide',
    element: (
      <PageWrapper skeleton="content">
        <GiftGuide />
      </PageWrapper>
    ),
  },
  {
    path: '/privacy-policy',
    element: (
      <PageWrapper skeleton="content">
        <PrivacyPolicy />
      </PageWrapper>
    ),
  },
  {
    path: '/terms-of-service',
    element: (
      <PageWrapper skeleton="content">
        <TermsOfService />
      </PageWrapper>
    ),
  },
  {
    path: '/search',
    element: (
      <PageWrapper skeleton="shop">
        <SearchResults />
      </PageWrapper>
    ),
  },
  {
    path: '/login',
    element: (
      <PageWrapper skeleton="auth">
        <Login />
      </PageWrapper>
    ),
  },
  {
    path: '/register',
    element: (
      <PageWrapper skeleton="auth">
        <Register />
      </PageWrapper>
    ),
  },
  {
    path: '/forgot-password',
    element: (
      <PageWrapper skeleton="auth">
        <ForgotPassword />
      </PageWrapper>
    ),
  },
  {
    path: '/reset-password',
    element: (
      <PageWrapper skeleton="auth">
        <ResetPassword />
      </PageWrapper>
    ),
  },
  {
    path: '/account',
    element: (
      <ProtectedRoute>
        <PageWrapper skeleton="content">
          <Account />
        </PageWrapper>
      </ProtectedRoute>
    ),
  },
  {
    path: '/orders',
    element: (
      <ProtectedRoute>
        <PageWrapper skeleton="default">
          <OrderHistory />
        </PageWrapper>
      </ProtectedRoute>
    ),
  },
  {
    path: '/wishlist',
    element: (
      <ProtectedRoute>
        <PageWrapper skeleton="default">
          <Wishlist />
        </PageWrapper>
      </ProtectedRoute>
    ),
  },
  {
    path: '*',
    element: (
      <PageWrapper skeleton="default">
        <NotFound />
      </PageWrapper>
    ),
  },
];
